import React, { useState, useRef, useEffect } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import Modal from "../../../../atoms/Modal";
import { ReactComponent as CloseIcon } from "../../../../assets/svg/close.svg";

import styles from "./UpdateProfileImageModal.module.scss";

interface UpdateProfileImageModalProps {
  isOpen: boolean;
  file: File | null;
  onClose: () => void;
  onSubmit: (croppedImage: File) => void;
  onDelete: () => void;
  calling: boolean;
}

const ASPECT_RATIO = 1;

export const UpdateProfileImageModal: React.FC<UpdateProfileImageModalProps> = ({
  isOpen,
  file,
  onClose,
  onSubmit,
  onDelete,
  calling,
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImageSrc(null);
    }
  }, [file]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        ASPECT_RATIO,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(crop);
  };

  const getCroppedImg = (
    image: HTMLImageElement,
    crop: PixelCrop,
    fileName: string,
  ) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * scaleX * pixelRatio;
    canvas.height = crop.height * scaleY * pixelRatio;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get canvas context");

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY,
    );

    canvas.toBlob(
      blob => {
        if (blob) {
          const file = new File([blob], fileName, { type: "image/jpeg" });
          onSubmit(file);
        }
      },
      "image/jpeg",
      1,
    );
  };

  const handleApply = () => {
    if (imgRef.current && completedCrop && file) {
      getCroppedImg(imgRef.current, completedCrop, file.name);
    }
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <Modal onClose={onClose} className={styles.modalContent}>
      <button className={styles.closeBtn} onClick={onClose}>
        <CloseIcon />
      </button>

      <div className={styles.container}>
        <div className={styles.cropContainer}>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={c => setCompletedCrop(c)}
            aspect={ASPECT_RATIO}
            circularCrop
          >
            <img
              ref={imgRef}
              alt="プロフィール画像"
              src={imageSrc}
              onLoad={onImageLoad}
              className={styles.sourceImage}
            />
          </ReactCrop>
        </div>
        <div className={styles.actions}>
          <button
            className={styles.deleteBtn}
            onClick={onDelete}
            disabled={calling}
          >
            現在の画像を削除
          </button>
          <button
            className={styles.applyBtn}
            onClick={handleApply}
            disabled={calling}
          >
            適用する
          </button>
        </div>
      </div>
    </Modal>
  );
};
